import React, { useState } from 'react';
import { XIcon, DownloadIcon, CheckIcon } from 'lucide-react';
interface ExportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  exportType: 'pdf' | 'csv';
  darkMode: boolean;
  availableFields: string[];
}
export const ExportDataModal: React.FC<ExportDataModalProps> = ({
  isOpen,
  onClose,
  exportType,
  darkMode,
  availableFields
}) => {
  const [selectedFields, setSelectedFields] = useState<string[]>(availableFields);
  const [draggedField, setDraggedField] = useState<string | null>(null);
  if (!isOpen) return null;
  const handleFieldToggle = (field: string) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter(f => f !== field));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  };
  const handleDragStart = (e: React.DragEvent, field: string) => {
    setDraggedField(field);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const handleDrop = (e: React.DragEvent, targetField: string) => {
    e.preventDefault();
    if (!draggedField || draggedField === targetField) return;
    const updatedFields = [...selectedFields];
    const draggedIndex = updatedFields.indexOf(draggedField);
    const targetIndex = updatedFields.indexOf(targetField);
    updatedFields.splice(draggedIndex, 1);
    updatedFields.splice(targetIndex, 0, draggedField);
    setSelectedFields(updatedFields);
    setDraggedField(null);
  };
  const handleExport = () => {
    // In a real application, this would generate and download the file
    console.log(`Exporting ${exportType.toUpperCase()} with fields:`, selectedFields);
    // Simulate download
    alert(`${exportType.toUpperCase()} export with selected fields is being downloaded.`);
    onClose();
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1351">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-1352">
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-1353">
          <div className={`absolute inset-0 ${darkMode ? 'bg-navy-900' : 'bg-gray-900'} opacity-75`} data-id="element-1354"></div>
        </div>
        {/* Modal Content */}
        <div className={`inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full ${darkMode ? 'bg-navy-800 text-white' : 'bg-white text-gray-800'}`} data-id="element-1355">
          <div className="px-6 py-5 flex items-center justify-between border-b border-gray-700" data-id="element-1356">
            <h3 className="text-xl font-medium" data-id="element-1357">
              Export Data as {exportType.toUpperCase()}
            </h3>
            <button onClick={onClose} className={`rounded-md ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'} p-1.5 transition-colors`} data-id="element-1358">
              <XIcon className="h-5 w-5" data-id="element-1359" />
            </button>
          </div>
          <div className="px-6 py-5" data-id="element-1360">
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-1361">
              Select and arrange the fields you want to include in your export.
              Drag and drop to change the order.
            </p>
            <div className={`p-4 rounded-lg mb-4 ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-1362">
              <h4 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`} data-id="element-1363">
                Available Fields
              </h4>
              <div className="space-y-2" data-id="element-1364">
                {availableFields.map(field => <div key={field} draggable={selectedFields.includes(field)} onDragStart={e => handleDragStart(e, field)} onDragOver={handleDragOver} onDrop={e => handleDrop(e, field)} className={`flex items-center justify-between p-2 rounded-lg ${selectedFields.includes(field) ? darkMode ? 'bg-navy-700 cursor-move' : 'bg-blue-50 cursor-move' : darkMode ? 'bg-navy-800' : 'bg-gray-100'} ${draggedField === field ? 'opacity-50' : 'opacity-100'}`} data-id="element-1365">
                    <div className="flex items-center" data-id="element-1366">
                      {selectedFields.includes(field) && <div className={`mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1367">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1368">
                            <line x1="5" y1="9" x2="19" y2="9" data-id="element-1369"></line>
                            <line x1="5" y1="15" x2="19" y2="15" data-id="element-1370"></line>
                            <line x1="5" y1="12" x2="19" y2="12" data-id="element-1371"></line>
                          </svg>
                        </div>}
                      <span className={`text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-1372">
                        {field}
                      </span>
                    </div>
                    <label className="inline-flex items-center" data-id="element-1373">
                      <input type="checkbox" checked={selectedFields.includes(field)} onChange={() => handleFieldToggle(field)} className={`form-checkbox h-4 w-4 rounded ${darkMode ? 'bg-navy-900 border-navy-700 text-deep-pink focus:ring-deep-pink' : 'bg-white border-gray-300 text-deep-pink focus:ring-deep-pink'}`} data-id="element-1374" />
                    </label>
                  </div>)}
              </div>
            </div>
            {selectedFields.length > 0 ? <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-1375">
                {selectedFields.length} field
                {selectedFields.length !== 1 ? 's' : ''} selected
              </p> : <p className="text-sm text-red-500 mb-4" data-id="element-1376">
                Please select at least one field to export
              </p>}
            <div className="flex justify-end space-x-3" data-id="element-1377">
              <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-1378">
                Cancel
              </button>
              <button type="button" onClick={handleExport} disabled={selectedFields.length === 0} className={`flex items-center px-4 py-2 rounded-lg transition-colors ${selectedFields.length === 0 ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-deep-pink hover:bg-burgundy text-white'}`} data-id="element-1379">
                <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1380" />
                Export {exportType.toUpperCase()}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};